import ChevronDownIcon from "@/components/_icons/chevronDownIcon";
import { daysOfWeek, OrderType } from "@/constants/enum";
import { DayPart } from "@/dto/dayPartMenu";
import { OrderEstimates } from "@/dto/orderEstimates";
import { ScheduleDateType, ScheduleTimeType } from "@/dto/scheduledTime";
import { HHMMtoDate, roundToNext15Minutes, toNativeDate } from "@/utils/dateTimeUtils";
import { getBusinessHoursOfDay } from "@/utils/scheduleOrderUtils";
import { getFromSessionStorage } from "@/utils/sessionStorageUtils";
import { SessionStorageKey } from "@/utils/storageKeys";
import { TIME_CONSTANTS } from "@/utils/constants";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { DateTime } from "luxon";
import { useTranslations, useLocale } from "next-intl";
import { useEffect, useMemo } from "react";

export type ScheduleTime = {
  orderType: string;
  date: ScheduleDateType;
  time: ScheduleTimeType;
};

export const ScheduledTimeDropdown = ({
  scheduleTime,
  setScheduleTime,
  businessHours,
  scheduleDate,
  leadTime,
  timezone,
  orderType,
  selectedDayPart = null,
}: {
  scheduleTime: ScheduleTimeType;
  // eslint-disable-next-line no-unused-vars
  setScheduleTime: (newTime: ScheduleTimeType) => void;
  businessHours: any;
  scheduleDate: ScheduleDateType;
  leadTime: any;
  timezone: string;
  orderType: string;
  selectedDayPart?: DayPart | null;
}) => {
  const t = useTranslations("scheduledTimeDropdown");
  const locale = useLocale();

  const { deliveryTime, pickupTime } = getFromSessionStorage<
    Partial<OrderEstimates>
  >(SessionStorageKey.ORDER_ESTIMATES, {});

  const localizedTimeLabel = useMemo(() => {
    const timeObj = scheduleTime?.timeObject;

    if (!timeObj) return scheduleTime.displayValue;

    let dt;

    if (typeof timeObj === "string") {
      // Initially: It is a String (ISO format)
      dt = DateTime.fromISO(timeObj);
    } else if (timeObj instanceof Date) {
      // After interaction: It becomes a native Date Object
      dt = DateTime.fromJSDate(timeObj);
    } else {
      return "";
    }

    return dt
      .setZone(timezone)
      .setLocale(locale)
      .toLocaleString(DateTime.TIME_SIMPLE);
  }, [scheduleTime, timezone, locale]);

  const timeOptions = useMemo(() => {
    const now = DateTime.now().setZone(timezone).setLocale(locale);
    const dayIndex = DateTime.fromJSDate(scheduleDate?.dateObject, {
      zone: timezone,
    }).weekday;
    const name = daysOfWeek[dayIndex % 7];
    const options: any[] = [];

    const deliveryTimeDate = deliveryTime
      ? new Date(deliveryTime).getTime()
      : 0;
    const pickUpTimeDate = pickupTime ? new Date(pickupTime).getTime() : 0;
    const deliveryDelay =
      orderType === OrderType.DELIVERY
        ? Math.round(
            (deliveryTimeDate - pickUpTimeDate) /
              TIME_CONSTANTS.MILLISECONDS_PER_SECOND /
              TIME_CONSTANTS.MINUTES_PER_HOUR
          )
        : 0;

    const todayTimeRanges = getBusinessHoursOfDay(
      businessHours,
      name,
      selectedDayPart
    );

    if (todayTimeRanges.length > 0) {
      const dateObj = scheduleDate?.dateObject;
      const currentDayInMerchantZone = DateTime.fromJSDate(dateObj, {
        zone: timezone,
      });

      todayTimeRanges.forEach(
        ({ start, end }: { start: string; end: string }) => {
          let startTime = HHMMtoDate(start.padStart(4, "0"), timezone, dateObj);
          const endTime = HHMMtoDate(end.padStart(4, "0"), timezone, dateObj);

          let leadTimeThreshold = roundToNext15Minutes(
            startTime.plus({ minutes: leadTime + deliveryDelay })
          );

          /**
           * BUG:
           * Lets take a case where
           * Start Time: 8:00 AM
           * Lead Time: 60 mins
           * Current Time is 8:45 AM
           * LeadTimeThreshold will be 9:00 AM which is greater than current time
           * So it will show 9:15AM as scheduled time where as food preperation time + current time will come to 9:45AM
           * Fix: calculating leadtime threshold with current time when currenttime is past start time
           */
          if (currentDayInMerchantZone.hasSame(now, "day") && startTime < now) {
            leadTimeThreshold = roundToNext15Minutes(
              now.plus({ minutes: leadTime + deliveryDelay })
            );
          }

          for (
            let time = leadTimeThreshold;
            time <= endTime;
            time = time.plus({ minutes: 15 })
          ) {
            if (!time.hasSame(currentDayInMerchantZone, "day")) continue;
            options.push({
              displayValue: time
                .setLocale(locale)
                .toLocaleString(DateTime.TIME_SIMPLE),
              timeObject: time.toJSDate(),
            });
          }
        }
      );
    }

    return options;
  }, [
    scheduleDate,
    businessHours,
    leadTime,
    timezone,
    orderType,
    selectedDayPart,
    locale,
    deliveryTime,
    pickupTime,
  ]);

  useEffect(() => {
    if (timeOptions.length === 0) return;

    const timeDay = scheduleTime?.timeObject
      ? DateTime.fromJSDate(toNativeDate(scheduleTime.timeObject))
      : null;
    const selectedDay = scheduleDate?.dateObject
      ? DateTime.fromJSDate(toNativeDate(scheduleDate.dateObject), { zone: timezone })
      : null;
    const isTimeOnWrongDay =
      !!timeDay && !!selectedDay && !timeDay.hasSame(selectedDay, "day");

    if (!scheduleTime?.displayValue || isTimeOnWrongDay) {
      setScheduleTime(timeOptions[0]);
    }
  }, [scheduleDate, orderType, timeOptions]);

  const isDisabled = timeOptions.length === 0;

  return (
    <div
      key={
        scheduleDate?.dateObject &&
        !Number.isNaN(new Date(scheduleDate.dateObject).getTime())
          ? new Date(scheduleDate.dateObject).toISOString()
          : "no-date"
      }
    >
      <Listbox value={scheduleTime} onChange={setScheduleTime}>
        {({ open }) => (
          <div className="relative">
            <ListboxButton
              className="flex items-center gap-x-2 w-full leading-6 text-gray-900 enabled:hover:bg-gray-50"
              aria-label={t("time")}
              disabled={isDisabled}
            >
              <input
                type="text"
                id="scheduled_time_drop"
                className="cursor-pointer peer text-[15px] leading-6 block w-full h-[52px] rounded-[4px] border border-black bg-transparent px-2 pt-6 pb-2 pr-10 text-gray-900 placeholder-transparent hover:border-black disabled:border-[#C7C7C7] disabled:text-[#C7C7C7] disabled:cursor-not-allowed tracking-wide"
                value={localizedTimeLabel}
                disabled={isDisabled}
                readOnly
              />
              <label
                htmlFor="scheduled_time_drop"
                className={`pt-0.5 text-xs leading-[18px] absolute left-2 top-3 origin-[0] -translate-y-2.5 tracking-wide ${
                  isDisabled
                    ? "text-[#C7C7C7] cursor-not-allowed"
                    : "text-[#727272] cursor-pointer"
                }`}
              >
                {t("time")}
              </label>
              {timeOptions.length > 1 && (
                <ChevronDownIcon
                  aria-hidden="true"
                  className={`cursor-pointer absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 transition-transform duration-300 ${
                    open ? "rotate-180" : "rotate-0"
                  }`}
                />
              )}
            </ListboxButton>
            <ListboxOptions
              transition
              className="absolute top-full z-20 w-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
              style={{ maxHeight: "400px", overflowY: "auto" }}
            >
              <div className="p-3">
                {timeOptions.map((time) => (
                  <ListboxOption
                    key={time.displayValue}
                    value={time}
                    className="group relative flex w-full items-center gap-x-6 rounded-lg p-3 leading-6 hover:bg-gray-50 data-[focus]:bg-gray-50 flex-auto cursor-pointer"
                    aria-label={time.displayValue}
                  >
                    <p className="text-[15px]">{time.displayValue}</p>
                  </ListboxOption>
                ))}
              </div>
            </ListboxOptions>
          </div>
        )}
      </Listbox>
    </div>
  );
};
